/**
 * Interface for required columns configuration
 */
export interface RequiredColumnsConfig {
  sheetName: string;
  requiredColumns: string[];
}

/**
 * Marks required columns with an asterisk (*) in the header row
 * @param sheetName - The name of the current sheet
 * @param jsonData - The 2D array of sheet data
 * @param config - Configuration object containing sheet name and required columns
 * @returns The updated jsonData with asterisks added to required column headers
 */
export function markRequiredColumnsWithAsterisk(
  sheetName: string,
  jsonData: any[][],
  config: RequiredColumnsConfig
): any[][] {
  if (!jsonData || jsonData.length === 0) {
    return jsonData;
  }

  // Check if the sheet matches the configuration
//   if (sheetName.toLowerCase() !== config.sheetName.toLowerCase()) {
//     return jsonData;
//   }

  const headerRow = jsonData[0];

  headerRow.forEach((header, index) => {
    const normalizedHeader = header.toString().toLowerCase().trim();
    if (config.requiredColumns.includes(normalizedHeader)) {
      headerRow[index] = `${header}*`;
    }
  });

  return jsonData;
}

/**
 * Predefined configuration for Stakeholders sheet
 */
export const STAKEHOLDERS_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'Stakeholders',
  requiredColumns: ['name', 'naam']
};

/**
 * Predefined configuration for Contact Persons sheet
 */
export const CONTACTPERSONS_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'Contactpersonen',
  requiredColumns: ['last_name', 'ernaam']
};

/**
 * Predefined configuration for Issues sheet
 */
export const ISSUES_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'Issues',
  requiredColumns: ['title', 'titel', 'date', 'datum', 'deadline', 'impact', 'invloed']
};

/**
 * Predefined configuration for Requirements sheet
 */
export const REQUIREMENTS_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'requirements',
  requiredColumns: ['title', 'titel', 'requirement_status']
};

/**
 * Predefined configuration for Contact Moments sheet
 */
export const CONTACTMOMENTS_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'contactmoments',
  requiredColumns: ['title', 'titel', 'date', 'datum', 'stakeholder 1', 'stakeholder_1']
};

/**
 * Predefined configuration for Complaints sheet
 */
export const COMPLAINTS_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'complaints',
  requiredColumns: ['date', 'datum', 'complaint', 'melding']
};

/**
 * Predefined configuration for Activities sheet
 */
export const ACTIVITIES_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'activiteiten',
  requiredColumns: ['name', 'naam', 'naam activiteit', 'end_date', 'einddatum', 'start_date', 'begindatum']
};

/**
 * Predefined configuration for Contents sheet
 */
export const CONTENTS_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'middelen',
  requiredColumns: ['type middel', 'type', 'date', 'datum', 'target_group', 'doelgroep(en)']
};

/**
 * Predefined configuration for Permits sheet
 */
export const PERMITS_REQUIRED_COLUMNS: RequiredColumnsConfig = {
  sheetName: 'permits',
  requiredColumns: ['permit', 'naam vergunning', 'permit name', 'name', 'naam', 'date', 'datum']
};

/**
 * Map of sheet names to their required columns configuration
 */
export const SHEET_REQUIRED_COLUMNS_MAP: { [key: string]: RequiredColumnsConfig } = {
  stakeholders: STAKEHOLDERS_REQUIRED_COLUMNS,
  contactpersonen: CONTACTPERSONS_REQUIRED_COLUMNS,
  contactpersons: CONTACTPERSONS_REQUIRED_COLUMNS,
  contact_persons: CONTACTPERSONS_REQUIRED_COLUMNS,
  issues: ISSUES_REQUIRED_COLUMNS,
  requirements: REQUIREMENTS_REQUIRED_COLUMNS,
  klanteisen: REQUIREMENTS_REQUIRED_COLUMNS,
  contactmoments: CONTACTMOMENTS_REQUIRED_COLUMNS,
  contact_moments: CONTACTMOMENTS_REQUIRED_COLUMNS,
  contactmomenten: CONTACTMOMENTS_REQUIRED_COLUMNS,
  complaints: COMPLAINTS_REQUIRED_COLUMNS,
  meldingen: COMPLAINTS_REQUIRED_COLUMNS,
  activiteiten: ACTIVITIES_REQUIRED_COLUMNS,
  activities: ACTIVITIES_REQUIRED_COLUMNS,
  middelen: CONTENTS_REQUIRED_COLUMNS,
  content: CONTENTS_REQUIRED_COLUMNS,
  contents: CONTENTS_REQUIRED_COLUMNS,
  permits: PERMITS_REQUIRED_COLUMNS,
  vergunningen: PERMITS_REQUIRED_COLUMNS
};

/**
 * Gets the required columns configuration for a given sheet name
 * @param sheetName - The name of the sheet
 * @returns The configuration object or null if no configuration found
 */
export function getRequiredColumnsConfig(sheetName: string): RequiredColumnsConfig | null {
  const key = sheetName.toLowerCase();
  return SHEET_REQUIRED_COLUMNS_MAP[key] || null;
}

/**
 * Applies required column marking to sheet data if configuration exists
 * @param sheetName - The name of the sheet
 * @param jsonData - The 2D array of sheet data
 * @returns The updated jsonData with asterisks applied if configuration exists
 */
export function applyRequiredColumnMarking(sheetName: string, jsonData: any[][]): any[][] {
  const config = getRequiredColumnsConfig(sheetName);
  if (!config) {
    return jsonData;
  }
  return markRequiredColumnsWithAsterisk(sheetName, jsonData, config);
}
